import type { ParameterDef } from '@quartup/reports-shared';
import { DateParam } from './date-param.js';
import { EnumParam } from './enum-param.js';
import { MultiRefParam } from './multi-ref-param.js';
import { NumberParam } from './number-param.js';
import { RefParam } from './ref-param.js';
import { StringParam } from './string-param.js';

export interface ParamInputProps {
  param: ParameterDef;
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean | undefined;
  error?: string | undefined;
}

export function ParamInput({ param, id, value, onChange, disabled, error }: ParamInputProps) {
  switch (param.type) {
    case 'date':
      return (
        <DateParam
          id={id}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          disabled={disabled}
          error={error}
          required={param.required}
        />
      );

    case 'number':
      return (
        <NumberParam
          id={id}
          value={typeof value === 'number' ? String(value) : typeof value === 'string' ? value : ''}
          onChange={(v) => onChange(v === '' ? '' : Number(v))}
          min={param.validation?.min}
          max={param.validation?.max}
          disabled={disabled}
          error={error}
          required={param.required}
        />
      );

    case 'string':
      return (
        <StringParam
          id={id}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          disabled={disabled}
          error={error}
          required={param.required}
        />
      );

    case 'boolean':
      return (
        <EnumParam
          id={id}
          value={value === true ? 'true' : value === false ? 'false' : ''}
          options={[
            { value: 'true', label: 'Sí' },
            { value: 'false', label: 'No' },
          ]}
          onChange={(v) => onChange(v === 'true' ? true : v === 'false' ? false : undefined)}
          disabled={disabled}
          error={error}
          required={param.required}
        />
      );

    case 'ref':
      if (param.multiple === true) {
        return (
          <MultiRefParam
            id={id}
            values={
              Array.isArray(value)
                ? (value as string[])
                : value !== undefined && value !== ''
                  ? [String(value)]
                  : []
            }
            onChange={onChange}
            refView={param.ref_view}
            refLabelField={param.ref_label_field}
            refValueField={param.ref_value_field}
            disabled={disabled}
            error={error}
            required={param.required}
          />
        );
      }
      return (
        <RefParam
          id={id}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          refView={param.ref_view}
          refLabelField={param.ref_label_field}
          refValueField={param.ref_value_field}
          disabled={disabled}
          error={error}
          required={param.required}
        />
      );

    default:
      return null;
  }
}
